import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const FitPostPrivacyPolicy = () => {
  return (
    <div style={styles.wrapper}>
      <Helmet>
        <title>Privacy Policy – FitPost</title>
        <meta name="description" content="Privacy Policy for FitPost – Learn how we collect, use, and protect your information when you use the FitPost health and fitness app." />
        <link rel="canonical" href="https://truth-ai-tech.com/fitpost/privacy-policy" />
      </Helmet>

      <article style={styles.article}>
        <p style={styles.intro}>
          FitPost is a health and fitness tracking app that syncs with Apple Health.
        </p>
        <p style={styles.backLinkWrap}>
          <Link to="/" style={styles.backLink}>← Truth AI Tech</Link>
        </p>

        <h1 style={styles.mainTitle}>FITPOST PRIVACY POLICY</h1>
        <p style={styles.lastUpdated}>Last Updated: February 2026</p>

        <p style={styles.paragraph}>
          FitPost ("we," "us," or "our") is based in Florida. This Privacy Policy explains how we collect, use, disclose, and protect information when you use the FitPost mobile application ("App") and related services. By using the App, you agree to this Privacy Policy. If you do not agree, please do not use the App.
        </p>

        <section style={styles.section}>
          <h2 style={styles.h2}>1. INFORMATION WE COLLECT</h2>

          <h3 style={styles.h3}>1.1 Account and Authentication</h3>
          <p style={styles.paragraph}>
            When you sign in with Apple or Google, we receive an identifier (user ID) and, depending on the provider and your choices, your name and email. We use this to create and manage your account. We do not receive or store your Apple or Google password.
          </p>

          <h3 style={styles.h3}>1.2 Profile Information</h3>
          <p style={styles.paragraph}>
            You may provide a display name, profile photo, daily step goal, and dashboard preferences (e.g., which stats to show or hide). Profile data is stored on your device and, when you are signed in, synced to our servers so it is available across devices and visible to other users as described below.
          </p>

          <h3 style={styles.h3}>1.3 Health and Activity Data</h3>
          <p style={styles.paragraph}>
            If you grant permission, the App reads data from Apple Health on your device, such as steps, sleep, and workouts. This data is used to display your progress in the App and to sync summary data (e.g., daily step counts) to our servers so you can participate in leaderboards and compare with friends in groups. We do not use health data for advertising or sell it to third parties.
          </p>

          <h3 style={styles.h3}>1.4 Usage and Device Information</h3>
          <p style={styles.paragraph}>
            We may collect information about how you use the App (e.g., features used) and technical data (e.g., device type, app version) to operate the App, fix issues, and improve our services. Our infrastructure providers (e.g., Firebase/Google) may also collect similar technical and usage data in accordance with their privacy policies.
          </p>

          <h3 style={styles.h3}>1.5 Data Stored on Your Device</h3>
          <p style={styles.paragraph}>
            We store your user ID (in the device keychain), profile data, onboarding and settings preferences, and terms-acceptance status on your device. This allows the App to work offline where possible and to keep you signed in.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>2. HOW WE USE YOUR INFORMATION</h2>
          <p style={styles.paragraph}>We use the information we collect to:</p>
          <ul style={styles.bulletList}>
            <li>Create and manage your account and authenticate you.</li>
            <li>Provide the App's features, including syncing your health and activity data, displaying your profile, and enabling groups and leaderboards.</li>
            <li>Sync your daily step counts (and related data you choose to share) so you can compare with friends and participate in group leaderboards.</li>
            <li>Store and display your profile (e.g., display name, profile photo) to you and to other members of groups you join.</li>
            <li>Send and process invite links so you can join or invite others to groups.</li>
            <li>Improve, secure, and troubleshoot the App and our systems.</li>
            <li>Comply with applicable law and enforce our Terms and Conditions.</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>3. SHARING AND DISCLOSURE</h2>

          <h3 style={styles.h3}>3.1 With Other Users</h3>
          <p style={styles.paragraph}>
            When you join a group, other members can see your display name and profile photo (if set). Step counts and other activity data you sync may be used for leaderboards and comparison features within that group. Only members of the same group can see this information. You control whether to join a group and what data is synced.
          </p>

          <h3 style={styles.h3}>3.2 With Service Providers</h3>
          <p style={styles.paragraph}>
            We use third-party services to operate the App:
          </p>
          <ul style={styles.bulletList}>
            <li><strong>Google Firebase</strong> (Authentication, Firestore database, Cloud Functions) — to authenticate you, store profile and activity data, and run server logic. Google's privacy policy applies to data processed by Firebase/Google.</li>
            <li><strong>Apple</strong> (Sign In, HealthKit) — for sign-in and to read health data you authorize. Apple's privacy policy and HealthKit terms apply.</li>
          </ul>
          <p style={styles.paragraph}>
            These providers may process data on our behalf and in accordance with their own policies. We do not sell your personal information.
          </p>

          <h3 style={styles.h3}>3.3 For Legal or Safety Reasons</h3>
          <p style={styles.paragraph}>
            We may disclose information if required by law, court order, or government request, or to protect the rights, property, or safety of FitPost, our users, or others.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>4. DATA RETENTION AND SECURITY</h2>

          <h3 style={styles.h3}>4.1 Retention</h3>
          <p style={styles.paragraph}>
            We retain your account and profile data for as long as your account is active. If you delete your account or ask us to delete your data, we will delete or anonymize it in accordance with our procedures and applicable law. Some data may remain in backups for a limited period before being overwritten.
          </p>

          <h3 style={styles.h3}>4.2 Security</h3>
          <p style={styles.paragraph}>
            We use industry-standard measures (including encryption in transit and access controls) to protect your data. No method of transmission or storage is 100% secure; we cannot guarantee absolute security.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>5. YOUR CHOICES AND RIGHTS</h2>

          <h3 style={styles.h3}>5.1 In the App</h3>
          <p style={styles.paragraph}>
            You can update or remove your profile information (e.g., display name, photo, step goal) in the App. You can revoke the App's access to Apple Health in your device's Health app settings. You can leave groups at any time, which limits what other members can see about you for group features.
          </p>

          <h3 style={styles.h3}>5.2 Account Deletion</h3>
          <p style={styles.paragraph}>
            You may delete your account through the App or by contacting us at truth.ai.tech@gmail.com. Deleting your account will remove your profile and associated data from our systems, subject to retention for legal or operational needs as described above.
          </p>

          <h3 style={styles.h3}>5.3 Privacy Rights (e.g., California, EU)</h3>
          <p style={styles.paragraph}>
            Depending on where you live, you may have additional rights, such as access, correction, deletion, portability, or opt-out of certain processing. To exercise these rights, contact us at truth.ai.tech@gmail.com. We will not discriminate against you for exercising your privacy rights. California residents: we do not sell personal information as defined under the CCPA.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>6. CHILDREN</h2>
          <p style={styles.paragraph}>
            The App is not intended for users under 13. We do not knowingly collect personal information from children under 13. If you believe we have collected such information, please contact us at truth.ai.tech@gmail.com and we will delete it.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>7. INTERNATIONAL USERS</h2>
          <p style={styles.paragraph}>
            Our services are operated from the United States. If you use the App from outside the United States, your information may be transferred to and processed in the United States. By using the App, you consent to such transfer and processing.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>8. CHANGES TO THIS POLICY</h2>
          <p style={styles.paragraph}>
            We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy in the App and updating the "Last Updated" date. Your continued use of the App after changes constitutes acceptance of the updated policy.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>9. CONTACT US</h2>
          <p style={styles.paragraph}>
            FitPost is based in Florida. For privacy-related questions, to exercise your rights, or to request deletion of your data, please contact us at <a href="mailto:truth.ai.tech@gmail.com" style={styles.emailLink}>truth.ai.tech@gmail.com</a> or through the support or contact options provided in the App or on our website.
          </p>
        </section>

        <footer style={styles.footer}>
          <Link to="/" style={styles.footerLink}>← Back to Truth AI Tech</Link>
        </footer>
      </article>
    </div>
  );
};

const styles = {
  wrapper: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
    padding: '24px 16px 48px',
  },
  article: {
    maxWidth: '720px',
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
  },
  intro: {
    fontSize: '15px',
    color: '#64748b',
    margin: '0 0 12px',
    lineHeight: 1.6,
  },
  backLinkWrap: {
    margin: '0 0 24px',
  },
  backLink: {
    color: '#007bff',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: 500,
  },
  mainTitle: {
    fontSize: '28px',
    fontWeight: 800,
    color: '#1e293b',
    margin: '0 0 8px',
    letterSpacing: '-0.5px',
    lineHeight: 1.25,
  },
  lastUpdated: {
    fontSize: '14px',
    color: '#64748b',
    margin: '0 0 24px',
    fontWeight: 500,
  },
  paragraph: {
    fontSize: '16px',
    lineHeight: 1.75,
    color: '#334155',
    margin: '0 0 16px',
  },
  section: {
    marginBottom: '40px',
  },
  h2: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#1e293b',
    margin: '0 0 16px',
    paddingBottom: '8px',
    borderBottom: '1px solid #e2e8f0',
  },
  h3: {
    fontSize: '17px',
    fontWeight: 600,
    color: '#334155',
    margin: '24px 0 10px',
  },
  bulletList: {
    margin: '0 0 16px',
    paddingLeft: '24px',
    fontSize: '16px',
    lineHeight: 1.75,
    color: '#334155',
  },
  emailLink: {
    color: '#007bff',
    textDecoration: 'none',
    fontWeight: 500,
  },
  footer: {
    marginTop: '48px',
    paddingTop: '24px',
    borderTop: '1px solid #e2e8f0',
  },
  footerLink: {
    color: '#007bff',
    textDecoration: 'none',
    fontSize: '15px',
    fontWeight: 500,
  },
};

export default FitPostPrivacyPolicy;
